import { reactive, nextTick, ref, Ref } from '@vue/composition-api';

import johaisetsuCarApi from '@/apis/johaisetsu/johaisetsu_car';
import johaisetsuReportApi from '@/apis/johaisetsu/settouSagyouReport/johaisetsu_report';
import johaisetsuSettouPatrolApi from '@/apis/johaisetsu/settouPatrol/settouPatrol/johaisetsu_settou_patrol';
import johaisetsuSettouOnsiteManagementApi from '@/apis/johaisetsu/settouPatrol/settouOnsiteManagement/johaisetsu_settou_onsite_management';

import {
  CarState,
  SearchState,
} from '@/models/johaisetsu/johaisetsuMap';
import { parseDateTimeString } from '@/lib/dateTimeUtil';
import {
  createInitialCarsData,
  convData,
  getCarKindChoices,
  shouldShowCar,
  deselectReportItems,
  loadJohaisetsuMtxs,
} from '@/components/Johaisetsu/JohaisetsuMap/utils';
import { createInjection } from '@/lib/createInjection';
import { IntervalID } from '@/lib/requestAnimationFrame';
import { JohaisetsuCarExt } from '@/models/johaisetsu/johaisetsuCar';
import { STATUS_STOPPED } from '@/lib/johaisetsu/settouSagyouReport/johaisetsuReportUtil';
import { JohaisetsuCarReport } from '@/models/apis/johaisetsu/settouSagyouReport/johaisetsuReportResponse';
import { JohaisetsuCarResponse } from '@/models/apis/johaisetsu/johaisetsuCarResponse';
import {
  JOHAISETSU_CAR_STATUS_WORKING,
  JOHAISETSU_CAR_STATUS_RUNNING,
  JOHAISETSU_CAR_STATUS_STOPPED,
} from '@/consts/johaisetsu_car';

const RECENT_DAYS = 60;

interface InjectedState {
  carState: CarState;
  searchState: SearchState;
}

export interface UseJohaisetsuCarsResult {
  carState: CarState;
  startCarUpdateInterval: () => Promise<void>;
  stopCarUpdateInterval: () => void;
  filterCars: () => void;
  getSelectedCar: () => JohaisetsuCarExt | null;
  selectCar: (carId: number) => Promise<void>;
  deselectCar: (carId: number) => boolean;
  deselectAllCars: () => void;
  clearCars: () => void;
  redrawCarLayerWatchValue: Ref<number>;
  notifyRedrawCarLayerNeeded: () => void;
}

const { provide, inject } = createInjection<InjectedState>('johaisetsuMap/useJohaisetsuCars');

export function provideJohaisetsuCars(searchState: SearchState): InjectedState {
  const carState = reactive<CarState>({
    cars: [],
    carsUpdatedAt: new Date(),
    carKindChoices: [],
  });
  const injectedState = { carState, searchState };
  provide(injectedState);
  return injectedState;
}

export default function useJohaisetsuCars(defaultInjectedState?: InjectedState): UseJohaisetsuCarsResult {
  const injectedState = defaultInjectedState ?? inject();
  const carState = injectedState.carState;
  const searchState = injectedState.searchState;
  let carUpdateTimer: IntervalID | null = null;
  const redrawCarLayerWatchValue = ref<number>(0);

  const startCarUpdateInterval = async() => {
    window.clearRequestInterval(carUpdateTimer);
    await refreshCars();
    carUpdateTimer = window.requestInterval(async() => {
      await refreshCars();
    }, 10000);
  };
  const stopCarUpdateInterval = () => {
    window.clearRequestInterval(carUpdateTimer);
  };

  const notifyRedrawCarLayerNeeded = () => {
    redrawCarLayerWatchValue.value += 1;
  };

  const filterCars = () => {
    // 検索ボタンが押された時に、検索条件を確定する
    searchState.decidedSearchConds = { ...searchState.searchConds };
    setCarShouldShow();
  };

  const setCarShouldShow = () => {
    carState.cars.forEach(car => {
      car.shouldShow = shouldShowCar(
        car,
        searchState.decidedSearchConds,
        searchState.johaisetsuRoleAreaMap,
        searchState.johaisetsuRoles,
        carState.carKindChoices,
      );
    });
  };

  const refreshCars = async() => {
    const { data: johaisetsuCars } = await johaisetsuCarApi.getJohaisetsuCars();
    const statusOrder = [
      JOHAISETSU_CAR_STATUS_WORKING,
      JOHAISETSU_CAR_STATUS_RUNNING,
      JOHAISETSU_CAR_STATUS_STOPPED,
    ];
    const findIndex = (status: string) => {
      const index = statusOrder.indexOf(status);
      return index === -1 ? statusOrder.length : index;
    };
    johaisetsuCars.sort((a, b) => {
      const indexA = findIndex(a.status);
      const indexB = findIndex(b.status);
      return indexA - indexB;
    });
    const recentTs = new Date().setDate(new Date().getDate() - RECENT_DAYS);
    const recentJohaisetsuCars = johaisetsuCars.filter(johaisetsuCar => {
      try {
        const tsDate = parseDateTimeString(johaisetsuCar.ts);
        return tsDate.getTime() > recentTs;
      } catch (e) {
        return false;
      }
    });
    // https://land-japan.slack.com/archives/C0684TML3GE/p1709905753710319?thread_ts=1709905319.428209&cid=C0684TML3GE
    // stoppedになってたら直ちに無視するのは少し判定が雑な気もしつつ(少し電波が途切れただけなどもありうるから)、
    // 一覧検索の結果であれば別に多少雑でも問題ないかという判断.
    const movingCars = recentJohaisetsuCars.filter(car => car.status !== STATUS_STOPPED);
    const settouPatrolCars: JohaisetsuCarResponse[] = [];
    const settouOnsiteManageMentCars: JohaisetsuCarResponse[] = [];
    const otherCars: JohaisetsuCarResponse[] = [];
    movingCars.forEach(car => {
      switch (car.johaisetsu_type) {
        case 'settou_patrol':
          settouPatrolCars.push(car);
          break;
        case 'settou_progress_manager_onsite_management':
        case 'settou_onsite_manager_onsite_management':
          settouOnsiteManageMentCars.push(car);
          break;
        default:
          otherCars.push(car);
          break;
      }
    });
    if (movingCars.length > 0) {
      const [generalReports, patrolReports, managerReports] = await Promise.all([
        fetchJohaisetsuReportsByCarIds(otherCars),
        fetchSettouPatrolsByCarIds(settouPatrolCars),
        fetchSettouOnsiteManagementsByCarIds(settouOnsiteManageMentCars),
      ]);
      const allReports: JohaisetsuCarReport[] = [...generalReports, ...patrolReports, ...managerReports];

      if (allReports.length > 0) {
        movingCars.forEach(car => {
          car.report = allReports.find(e => e.device_id === car.device_id) ?? null;
        });
      }
    }

    // carState.carsが空の場合は固定値、2回目以降は自分で持ってるやつ
    const isInitial = carState.cars.length === 0;
    if (isInitial) {
      carState.cars = createInitialCarsData(recentJohaisetsuCars);
    }
    const currentCarInfoMap = Object.fromEntries(
      carState.cars.map(e => [
        e.id,
        {
          isSelected: e.isSelected,
          selectedMtxTs: e.reportExt?.mtxsExt.find(mtx => mtx.isSelected)?.ts ?? '',
          loadedMtxs: e.reportExt?.mtxsExt ?? [],
        },
      ]),
    );
    const master = await window.master.$promise;
    const carKindMap = master.lovs.car_kind.map;
    carState.cars = convData({
      cars: recentJohaisetsuCars,
      currentCarInfoMap,
      carKindMap,
      johaisetsuRoles: master.johaisetsuRoles.vals,
    });
    carState.carKindChoices = getCarKindChoices(carState.cars, carState.carKindChoices, carKindMap);
    setCarShouldShow();
    carState.carsUpdatedAt = new Date();
    nextTick(() => {
      notifyRedrawCarLayerNeeded();
    });
  };

  async function fetchJohaisetsuReportsByCarIds(cars: JohaisetsuCarResponse[]): Promise<JohaisetsuCarReport[]> {
    if (cars.length === 0) return [];

    const { data } = await johaisetsuReportApi.getByCarIds({
      johaisetsu_car_ids: cars.map(car => car.id),
    });
    return data;
  }

  async function fetchSettouPatrolsByCarIds(cars: JohaisetsuCarResponse[]): Promise<JohaisetsuCarReport[]> {
    if (cars.length === 0) return [];

    const { data } = await johaisetsuSettouPatrolApi.getByCarIds({
      johaisetsu_car_ids: cars.map(car => car.id),
    });
    return data;
  }

  async function fetchSettouOnsiteManagementsByCarIds(cars: JohaisetsuCarResponse[]): Promise<JohaisetsuCarReport[]> {
    if (cars.length === 0) return [];

    const { data } = await johaisetsuSettouOnsiteManagementApi.getByCarIds({
      johaisetsu_car_ids: cars.map(car => car.id),
    });
    return data;
  }

  const getSelectedCar = (): JohaisetsuCarExt | null => {
    return carState.cars.find(e => e.isSelected) ?? null;
  };

  const selectCar = async(carId: number): Promise<void> => {
    carState.cars.forEach(car => {
      car.isSelected = car.id === carId;
    });

    const car = getSelectedCar();
    const report = car?.reportExt;
    if (report && !report.isMtxsLoaded) {
      await loadJohaisetsuMtxs([report]);
    }
  };

  const deselectAllCars = () => {
    carState.cars.forEach(car => {
      car.isSelected = false;
    });
  };

  const deselectCar = (carId: number): boolean => {
    const car = getSelectedCar();
    if (!car || car.id !== carId) return false;

    car.isSelected = false;
    if (car.reportExt) {
      deselectReportItems(car.reportExt);
    }
    return true;
  };

  const clearCars = (): void => {
    carState.cars = [];
  };

  return {
    carState,
    startCarUpdateInterval,
    stopCarUpdateInterval,
    filterCars,
    getSelectedCar,
    selectCar,
    deselectCar,
    deselectAllCars,
    clearCars,
    redrawCarLayerWatchValue,
    notifyRedrawCarLayerNeeded,
  };
}
